import BodyText from 'components/Luxkit/Typography/BodyText'
import UltraLuxTransferModal from 'components/Pages/TypesPage/UltraLux/UltraLuxTransferModal'
import useToggle from 'hooks/useToggle'
import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'
import TextLink from 'components/Luxkit/TextLink'
import Image from 'components/Common/Image'
import LinePhoneIcon from 'components/Luxkit/Icons/line/LinePhoneIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useUltraLuxContact from 'hooks/Offers/Ultralux/useUltraluxContact'
import { useContext } from 'react'
import GeoContext from 'contexts/geoContext'
import { UltraluxUSPDefinitions } from 'constants/ultraLux'
import Group from 'components/utils/Group'

const USPContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(24)};
  padding-top: ${rem(40)};
  border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  align-items: center;

  ${mediaQueryUp.desktop} {
    display: flex;
    gap: ${rem(40)};
    padding-top: ${rem(24)};
  }
`

const USP = styled(Group)`
  ${mediaQueryUp.tablet} {
    max-width: ${rem(330)};
  }
`

const ImageWrapper = styled.div`
  min-width: ${rem(68)};
  height: ${rem(68)};

  ${mediaQueryUp.tablet} {
    min-width: ${rem(84)};
    height: ${rem(84)};
  }
`

interface Props {
  isPrivateRequest?: boolean
  onRequestPrivateTour?: () => void
}

function UltraLuxUSPs(props: Props) {
  const {
    isPrivateRequest,
    onRequestPrivateTour,
  } = props
  const [transferModalOpen, toggleTransferModalOpen] = useToggle()

  const { currentRegionCode } = useContext(GeoContext)
  const canOfferTransfers = currentRegionCode === 'AU'
  const contact = useUltraLuxContact()

  const serviceUsp = UltraluxUSPDefinitions.hotels.service(currentRegionCode)
  const transferUsp = UltraluxUSPDefinitions.hotels.transfers(currentRegionCode)

  return <>
    <USPContainer>
      <USP
        gap={16}
        tabletGap={20}
        direction="horizontal-reverse"
        tabletDirection="horizontal"
        verticalAlign="start"
        tabletVerticalAlign="center"
        horizontalAlign="space-between"
        tabletHorizontalAlign="start"
      >
        <ImageWrapper>
          <Image fit="center" id={serviceUsp.imageId} />
        </ImageWrapper>
        <Group direction="vertical" gap={4}>
          <BodyText variant="medium" weight="bold">
            {serviceUsp.title}
          </BodyText>
          <BodyText variant="medium" colour="neutral-three">
            {serviceUsp.subtitle}
          </BodyText>
          <TextLink
            size="medium"
            weight="bold"
            href={`tel:${contact.number}`}
            startIcon={<LinePhoneIcon />}
          >
            {contact.humanReadable}
          </TextLink>
        </Group>
      </USP>
      {canOfferTransfers && (
        <USP
          gap={16}
          tabletGap={20}
          direction="horizontal-reverse"
          tabletDirection="horizontal"
          verticalAlign="start"
          tabletVerticalAlign="center"
          horizontalAlign="space-between"
          tabletHorizontalAlign="start"
        >
          <ImageWrapper>
            <Image fit="center" id={transferUsp.imageId} />
          </ImageWrapper>
          <Group direction="vertical" gap={4}>
            <BodyText variant="medium" weight="bold">
              {transferUsp.title}
            </BodyText>
            <BodyText variant="medium" colour="neutral-three">
              {transferUsp.subtitle}
            </BodyText>
            <TextLink size="medium" onClick={toggleTransferModalOpen}>
              Learn more
            </TextLink>
          </Group>
        </USP>
      )}
      {isPrivateRequest && (
        <USP
          gap={16}
          tabletGap={20}
          direction="horizontal-reverse"
          tabletDirection="horizontal"
          verticalAlign="start"
          tabletVerticalAlign="center"
          horizontalAlign="space-between"
          tabletHorizontalAlign="start"
        >
          <ImageWrapper>
            <Image fit="center" id="private-request-20230523" />
          </ImageWrapper>
          <Group direction="vertical" gap={4}>
            <BodyText variant="medium" weight="bold" colour="neutral-two">
              Make this a private departure
            </BodyText>
            <BodyText variant="medium" colour="neutral-three">
              Tailor the itinerary to your group's interests and travel when works for you.
            </BodyText>
            <TextLink size="medium" weight="bold" onClick={onRequestPrivateTour}>Request private departure</TextLink>
          </Group>
        </USP>
      )}
    </USPContainer>
    <UltraLuxTransferModal
      open={transferModalOpen}
      onClose={toggleTransferModalOpen}
    />
  </>
}

export default UltraLuxUSPs
